// src/components/product/ProductCard.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgePercent, PackageX, Star, Tag } from "lucide-react";
import FavoriteButton from "../../../../components/Common/FavoriteButton/FavoriteButton";

export type ProductCardProps = {
  id: number;
  ad: string;

  img?: string | null;

  fotoMain?: string | null;
  fotoList?: string[] | null;

  fiyat: number;
  paraBirimi?: string;

  indirimliFiyat?: number | null;
  indirimAktifMi?: boolean | null;

  listeFiyati?: number | null;

  oneCikanmi?: boolean | null;
  stokAdedi?: number | null;

  kategoriAd?: string | null;

  badgeText?: string;
};

const clamp2: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

function money(v: number, currency: string) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(v);
}

function getFirstImage(p: ProductCardProps) {
  return p.img ?? p.fotoMain ?? p.fotoList?.[0] ?? null;
}

function computePricing(p: ProductCardProps) {
  const currency = p.paraBirimi || "TRY";
  const normal = typeof p.fiyat === "number" ? p.fiyat : 0;

  const discountCandidate =
    typeof p.indirimliFiyat === "number" ? p.indirimliFiyat : null;

  const isDiscount =
    (p.indirimAktifMi ?? true) &&
    discountCandidate != null &&
    normal > 0 &&
    discountCandidate > 0 &&
    discountCandidate < normal;

  const sale = isDiscount ? (discountCandidate as number) : normal;

  const pct =
    isDiscount && normal > 0 ? Math.round(((normal - sale) / normal) * 100) : null;

  return {
    currency,
    normal,
    sale,
    isDiscount,
    pct,
    normalText: money(normal, currency),
    saleText: money(sale, currency),
  };
}

const ProductCard: React.FC<ProductCardProps> = (props) => {
  const { id, ad, stokAdedi, oneCikanmi, badgeText, kategoriAd } = props;

  const [imgOk, setImgOk] = useState(true);

  const imgSrc = useMemo(() => getFirstImage(props), [props.img, props.fotoMain, props.fotoList]);

  const P = useMemo(() => computePricing(props), [
    props.fiyat,
    props.paraBirimi,
    props.indirimliFiyat,
    props.indirimAktifMi,
  ]);

  const outOfStock = typeof stokAdedi === "number" ? stokAdedi <= 0 : false;

  return (
    <Link
      to={`/urun/${id}`}
      aria-label={ad}
      className={[
        "group relative block overflow-hidden rounded-2xl border",
        "bg-white border-slate-200",
        "shadow-[0_1px_2px_rgba(15,23,42,0.06)]",
        "transition-all duration-200",
        outOfStock
          ? "opacity-95"
          : "hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,23,42,0.10)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/25",
      ].join(" ")}
    >
      {/* ==== IMAGE ==== */}
      <div className="relative aspect-[4/4] bg-slate-50">
        {/* subtle shine */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-black/[0.03]" />

        {imgSrc && imgOk ? (
          <img
            src={imgSrc}
            alt={ad}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImgOk(false)}
            className={[
              "absolute inset-0 h-full w-full object-cover object-center",
              outOfStock ? "" : "transition-transform duration-300 group-hover:scale-[1.03]",
            ].join(" ")}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-sm text-slate-500">
            Görsel yok
          </div>
        )}

        {/* left badges */}
        <div className="absolute left-3 top-3 flex flex-col gap-2">
          {kategoriAd && (
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-slate-800 backdrop-blur">
              <Tag className="h-3.5 w-3.5" />
              {kategoriAd}
            </span>
          )}

          {badgeText && (
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-slate-900 shadow-sm backdrop-blur">
              {badgeText}
            </span>
          )}

          {oneCikanmi && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
              <Star className="h-3.5 w-3.5" />
              Öne Çıkan
            </span>
          )}
        </div>

        {/* right badges */}
        <div className="absolute left-3 top-3 flex flex-col items-end gap-2">
          {P.isDiscount && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
              <BadgePercent className="h-3.5 w-3.5" />
              {P.pct ? `%${P.pct}` : "İndirim"}
            </span>
          )}
        </div>
        {outOfStock && (
  <div className="absolute inset-0 z-20 grid place-items-center">
    {/* arka plan karartma */}
    <div className="absolute inset-0 bg-black/35 backdrop-blur-[1px]" />

    {/* orta etiket */}
    <div className="relative inline-flex text-center items-center gap-2 rounded w-[12rem]  bg-white/80 px-14 py-2 text-[15px] font-normal  tracking-widest text-slate-900 shadow-lg ring-1 ring-black/5">
      TÜKENDİ
    </div>
  </div>
)}

        {/* Favori butonu — sağ üst köşe */}
        <div className="absolute right-2.5 top-2.5 z-10">
          <FavoriteButton urunId={id} size={16} />
        </div>

        {/* bottom fade for text readability */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/10 to-transparent" />
      </div>

      {/* ==== CONTENT ==== */}
      <div className="p-4 sm:p-5">
        {/* title */}
        <div
          className="text-[14px] sm:text-[15px] font-semibold leading-snug text-slate-900"
          style={clamp2}
          title={ad}
        >
          {ad}
        </div>
<div className=" w-full mt-1 flex items-end justify-start gap-2">
{P.isDiscount && (
              <div className="   gap-2">
                <div className="text-[12px] text-slate-500 line-through">
                  {P.normalText}
                </div>
              </div>
            )}
                        <div className="mt-0.5  gap-2">
              <div
                className={[
                  "font-normal tracking-tight",
                  P.isDiscount
                    ? "text-[20px] sm:text-[22px] text-slate-900"
                    : "text-[18px] sm:text-[20px] text-slate-500",
                ].join(" ")}
              >
                {P.saleText}
              </div>


            </div>
</div>


        {/* divider */}
        <div className="mt-4 h-px w-full bg-slate-100" />

        {/* action row */}
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[12px] text-slate-500">
            {P.isDiscount ? "İndirimli ürün" : "Hızlı incele"}
          </div>

          <span className="inline-flex items-center gap-2 text-[12px] font-semibold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
            İncele
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
          </span>
        </div>
      </div>

      {/* bottom accent (minimal) */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-600 to-teal-500 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
};

export default ProductCard;
